import type { NavigateFunction } from 'react-router-dom';

import toast from 'components/partials/toast/toast';

import type { ApiErrorWithMeta } from 'api/interceptors';

import { transformUserFormData } from 'helpers/forms';
import type { UserFormValues } from 'helpers/forms';

import { UNAUTHORIZED } from 'constants/error-types';
import {
  USER_NOT_SAVED_FALLBACK_MESSAGE,
  USER_NOT_SAVED_TITLE,
} from 'containers/admin/users/user/user-profile/user-profile.constants';

interface UserLike {
  id?: string | number;
  authority?: string;
  name?: string;
  client?: { id?: string | number; [key: string]: unknown } | null;
  [key: string]: unknown;
}

interface ClientLike {
  id?: string | number;
  name?: string;
  [key: string]: unknown;
}

interface UsersApi {
  createOrUpdate: (data: Record<string, unknown>) => Promise<unknown>;
}

interface CurrentUserLike {
  data?: { id?: string | number };
}

interface OnUserFormSubmitArgs {
  formData: UserFormValues;
  navigate: NavigateFunction;
  redirectPath: string;
  users: UsersApi;
  user: UserLike | null;
  currentUser: CurrentUserLike;
  isCurrentUserProfile?: boolean;
  reloadUser: () => void;
}

export const onUserFormSubmit = ({
  formData,
  navigate,
  redirectPath,
  users,
  user,
  currentUser,
  isCurrentUserProfile,
  reloadUser,
}: OnUserFormSubmitArgs): Promise<unknown> => {
  const dataToSend = transformUserFormData(
    formData,
    currentUser as { id?: string | number } | null | undefined
  );

  if (isCurrentUserProfile && user) dataToSend.authority = user.authority;

  return users.createOrUpdate(dataToSend).then(
    () => {
      toast.success({
        title: `User ${formData?.id ? 'updated' : 'created'}`,
        message: formData?.id
          ? `You've successfully updated ${formData?.firstName} ${formData?.lastName}`
          : `You've successfully added ${formData?.firstName} ${formData?.lastName} as a user`,
      });
      navigate(redirectPath);
      if (formData?.id) {
        reloadUser();
      }
    },
    (err: unknown) => {
      const apiError = err as ApiErrorWithMeta | null | undefined;

      if (apiError?.status !== UNAUTHORIZED) {
        toast.error({
          title: USER_NOT_SAVED_TITLE,
          message: apiError?.data?.detail || USER_NOT_SAVED_FALLBACK_MESSAGE,
        });
      }
      console.error(err);
      throw err;
    }
  );
};

export interface BreadcrumbItem {
  label?: string;
  href?: string;
  isActive?: boolean;
}

export const getBreadcrumbsForUserPage = (
  isUserPage: boolean,
  currentClient: ClientLike | null | undefined,
  user: UserLike | null | undefined
): BreadcrumbItem[] => {
  const baseBreadcrumbs: BreadcrumbItem[] = isUserPage
    ? [{ label: 'Users', href: '/admin/users' }]
    : [
        { label: 'Clients', href: '/admin/clients' },
        {
          label: currentClient?.name,
          href: `/admin/clients/${currentClient?.id}/users`,
        },
        {
          label: 'Users',
          href: `/admin/clients/${currentClient?.id}/users`,
        },
      ];

  baseBreadcrumbs.push({
    label: user?.name || 'New user',
    isActive: true,
  });

  return baseBreadcrumbs;
};

interface GetRedirectPathForUserArgs {
  setIsFormActive?: (active: boolean) => void;
  user?: UserLike | null;
  currentClient?: ClientLike | null;
}

export const getRedirectPathForUser = ({
  setIsFormActive,
  user,
  currentClient,
}: GetRedirectPathForUserArgs): string => {
  const isUserPage = location.pathname.includes('/admin/users');

  const newUserRedirectPath = isUserPage
    ? '/admin/users'
    : `/admin/clients/${currentClient?.id}/users`;

  const editUserRedirectPath = isUserPage
    ? `/admin/users/${user?.id}/profile`
    : `/admin/clients/${user?.client?.id}/users/${user?.id}/profile`;

  return !!setIsFormActive && !!user
    ? editUserRedirectPath
    : newUserRedirectPath;
};
